import type { IDistrict } from '@dash/district';
import type {
  AmendmentTimelineStatus,
  CombinationStatus,
  ContractActionKey,
  ContractPricing,
  ContractStatus,
  ContractTabActionKey,
  GateCode,
  RateLineSide,
  UnresolvedReason,
} from './constants';

// `name` arrives already localized — render it; `id` is only there to branch on.
interface IEnumPair<TId extends string = string> {
  id: TId;
  name: string;
}

export interface INamedRef {
  id: string;
  name: string;
}

export interface IEndpointAction {
  key: ContractActionKey;
  url?: string;
  method?: string;
  options?: {
    starts_on?: string;
    gate_passed?: boolean;
    // On a tab action: how that tab's amendment is priced. Absent from a server that does not say it.
    pricing?: ContractPricing;
    locked?: boolean;
  } & Record<string, unknown>;
}

export interface IGateCondition {
  code: GateCode;
  message: string;
  data: {
    side?: RateLineSide;
    rate_card_id?: string;
    uncovered_count?: number;
    suspended_count?: number;
    total_count?: number;
  };
}

export type IGate = { passed: true; unmet_conditions: [] } | { passed: false; unmet_conditions: IGateCondition[] };

interface IResolvedEngineVersion {
  id: string;
  name: string;
  status: IEnumPair;
  effective_from: string;
}

interface IContractRateCard {
  id: string;
  name: string;
  effective_from: string;
  status: IEnumPair;
  has_pay_lines: boolean;
  has_bill_lines: boolean;
}

export interface IContractAmendment {
  id: string;
  starts_on: string;
  timeline_status: IEnumPair<AmendmentTimelineStatus>;
  units: INamedRef[];
}

export interface IContractInvite {
  id: string;
  status: IEnumPair;
  sent_at: string | null;
  activated_at: string | null;
}

export interface IContractAgencyContact {
  id: string;
  name: string;
  email: string;
}

export interface IContractAgency {
  id: string;
  name: string;
  // Street, city and state code joined with ", " — "" when every part is blank.
  address: string;
  street: string | null;
  city: string | null;
  // The state code, not its name; null when the agency has none (always, where the country keeps no states).
  state: string | null;
  zip: string | null;
  // Paperclip's placeholder ("nophoto") when nothing was uploaded, never null.
  logo: string;
  contact: IContractAgencyContact | null;
}

export interface IContractPageBody {
  id: string;
  status: ContractStatus;
  preference_level: number | null;
  created_at: string;
  // Served, deliberately not shown: it is the day *after* the last booked shift under this contract,
  // so no sentence written around it reads the date it appears to carry.
  latest_booking_starts_on: string | null;
  agency: IContractAgency;
  legal_entity: INamedRef | null;
  invite: IContractInvite | null;
  pricing_engine: INamedRef;
  resolved_engine_version: IResolvedEngineVersion | null;
  rate_card: IContractRateCard | null;
  // Whether pension is charged for workers under 21. Absent from a server without the pension waiver.
  charge_pension_regardless_of_age?: boolean;
  gate: IGate;
  amendment: IContractAmendment;
  actions: IEndpointAction[];
  // How the shown amendment is priced; absent from a server that does not say it. A legacy amendment of a converted
  // contract comes in this engine body too, with empty units and its districts and rates in `districts`.
  pricing?: ContractPricing;
  districts?: Omit<IDistrict, 'new_zip_codes' | 'apply_markup_to_positions'>[];
}

// A legacy contract's answer on the same route. Deliberately not modelled: nothing here reads it. It says
// `pricing: 'legacy'` too, so it is told apart from a legacy tab by `pricing_engine`, never by `pricing`.
interface ILegacyContractBody {
  id: string;
  pricing_engine?: undefined;
  [key: string]: unknown;
}

export type ContractShowBody = IContractPageBody | ILegacyContractBody;

// Truthiness, not `in`: the legacy member declares the key as optional, so `in` would not narrow.
export const isEngineBoundBody = (body: ContractShowBody): body is IContractPageBody => Boolean(body.pricing_engine);

interface IScopeUnresolved {
  side: RateLineSide;
  parity_status: IEnumPair | null;
  reason: IEnumPair<UnresolvedReason>;
}

export interface IScopeCombination {
  unit: { id: string; name: string; zip: string };
  position: INamedRef;
  // True only when an `agency_contract_id` was sent, so it is false throughout the create form.
  in_scope: boolean;
  // Every parity variant resolves on both sides — exactly when `unresolved` is null.
  covered: boolean;
  // The pay base rate at pre-parity. A row can carry one and still be uncovered.
  rate: string | null;
  unresolved: IScopeUnresolved[] | null;
}

export interface IScopeCombinationsResponse {
  effective_on: string;
  resolved_engine_version: Pick<IResolvedEngineVersion, 'id' | 'name' | 'effective_from'> | null;
  rate_card: Pick<IContractRateCard, 'id' | 'name' | 'effective_from' | 'status'> | null;
  records: IScopeCombination[];
}

// Always the complete desired scope, never a delta.
export interface IScopeUnitPayload {
  unit_id: string;
  position_ids: string[];
}

export interface IScopeDelta {
  added_count: number;
  removed_count: number;
}

export interface IContractVersionRef {
  tab: ContractTabActionKey;
  url: string;
}

export interface IRateLineHeader {
  key: string;
  // Carries its own unit as a suffix, e.g. "Base Pay (£)" / "Markup (%)".
  name: string;
}

export interface IRateLine {
  // No id: the wire's combination id is not unique per row.
  unit: INamedRef;
  position: INamedRef;
  job_type: INamedRef | null;
  // Absent on a side with no parity axis (tenure off, or no parity lookup in that side's chain); null when the axis
  // exists but the row carries no status.
  parity_status?: IEnumPair | null;
  status: IEnumPair<CombinationStatus>;
  // This side's coverage only.
  covered: boolean;
  line_id: string | null;
  // Keyed by header.key.
  values: Record<string, string | null>;
  unresolved_reason: IEnumPair<UnresolvedReason> | null;
}

export interface IGridFilter {
  name: string;
  type: string;
  filterOptions: IEnumPair[];
  activeFilters: string[];
}

export interface IAgencyOption {
  id: string;
  name: string;
  contracted_legal_entity_ids: string[];
}

// A state as the pickers keep it, built from a `states/list` row (`{ id, code }` — the grid emits no
// name, so `name` is the code). `code` is what the create endpoint resolves an agency's state against.
export interface IStateOption {
  id: string;
  name: string;
  code: string;
}

export interface IBlockedRemoval {
  unit: INamedRef;
  position: INamedRef;
  booking_count: number;
  earliest_starts_at: string;
}

export type ParsedErrorMap = Record<string, string[]>;
